import { Icon } from '@iconify/react';
import {
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { generateClient } from 'aws-amplify/api';
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { Schema } from '../../../../../../amplify/data/resource';
import Select from '../../../../../components/custom/Select';
import TemplateTextEditor from './TemplateTextEditor';
import WhatsAppDialog from './WhatsAppDialog';

const sidebarVariants = {
  hidden: { x: '-100%', transition: { duration: 0.5 } },
  visible: { x: 0, transition: { duration: 0.5 } },
};

// @ts-ignore
type Template = Schema['WhatsAppTemplate']['type'];

const apiClient = generateClient<Schema>();

interface Props {
  open: boolean;
  onClose: () => void;
}

const WhatsAppSidebar: React.FC<Props> = ({ open, onClose }) => {
  const theme = useTheme();
  const [selectedValue, setSelectedValue] = useState('');

  const [templates, setTemplates] = useState<Template[]>([]);

  const [finalText, setFinalText] = useState<string>('');

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const subscription =
          apiClient.models.WhatsAppTemplate.observeQuery().subscribe({
            next: ({ items }: { items: any[] }) => {
              setTemplates(items);
            },
            error: (error: any) => {
              console.error('Error fetching clients:', error);
            },
          });

        return () => subscription.unsubscribe();
      } catch (error) {
        console.error('Error during fetchUsers:', error);
      }
    };

    fetchTemplates();
  }, []);

  const [settingsDialog, setSettingsDialog] = useState(false);

  const handleOpenSettingsDialog = () => {
    setSettingsDialog(true);
  };

  const handleCloseSettingsDialog = () => {
    setSettingsDialog(false);
  };

  const handleChange = (value: string) => {
    setSelectedValue(value);
  };

  const sidebarRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.whatsapp-dialog')
      ) {
        onClose();
      }
    };

    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [open, onClose]);

  const handleSelectClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
  };

  return (
    <motion.div
      ref={sidebarRef}
      initial="hidden"
      animate={open ? 'visible' : 'hidden'}
      exit="hidden"
      variants={sidebarVariants}
      style={{
        width: '430px',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: '#FFF',
        zIndex: 1200,
      }}
    >
      <Stack
        sx={{
          width: '100%',
          height: '100%',
          py: '45px',
          px: '25px',
          flexDirection: 'column',
          gap: '24px',
          overflowY: 'auto',
          boxSizing: 'border-box',
        }}
      >
        <Stack
          direction="row"
          justifyContent="flex-end"
          alignItems="center"
          spacing={1}
          sx={{ mt: -3 }}
        >
          <IconButton onClick={handleOpenSettingsDialog}>
            <Icon
              icon="mdi:cog-outline"
              color="#343434"
              width="24"
              height="24"
            />
          </IconButton>

          <IconButton onClick={onClose}>
            <Icon icon="mdi:close" color="#343434" width="24" height="24" />
          </IconButton>
        </Stack>

        <div onClick={handleSelectClick}>
          <Select
            label="בחר תבנית הודעה"
            value={selectedValue}
            id="template-select"
            onChange={(value: string) => handleChange(value)}
            MenuProps={{
              container: sidebarRef.current,
              sx: {
                '& .MuiMenuItem-root': {
                  textAlign: 'right',
                  direction: 'rtl',
                },
              },
            }}
            options={templates.map((template) => ({
              value: template.id,
              label: template.title || '',
            }))}
          />
        </div>
        <TemplateTextEditor
          text={
            selectedValue
              ? templates.find((template) => template.id === selectedValue)
                  ?.text || 'לא נמצא תוכן לתבנית שנבחרה'
              : 'בחר תבנית כדי להציג את התוכן שלה'
          }
          onVariableChange={(updatedVariables) => {
            const updatedText = selectedValue.replace(
              /{{\d+}}/g,
              (match: string) => {
                return updatedVariables[match] || match;
              },
            );

            setFinalText(updatedText || '');
          }}
        />
        <Stack spacing={1} direction="row" alignItems="center">
          <Button
            sx={{
              width: '45px',
              height: '45px',
              minWidth: '45px',
              minHeight: '45px',
              justifyContent: 'center',
              alignItems: 'center',
              display: 'flex',
              backgroundColor: '#B6F829',
              padding: 0,
              borderRadius: '8px',
              boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
              '&:hover': {
                backgroundColor: '#f0f0f0',
              },
            }}
            type="submit"
          >
            <img
              alt="auth"
              src="/icons/auth.svg"
              width="22.3px"
              height="25.36px"
              style={{ objectFit: 'contain' }}
            />
          </Button>
          <Typography
            fontSize="16px"
            fontWeight="700"
            fontFamily={'AlmoniDL'}
            color={theme.palette.dark.main}
          >
            שלח הודעה
          </Typography>
        </Stack>
        <Divider />
        <Typography
          fontSize="28px"
          fontWeight="700"
          fontFamily={'AlmoniDL'}
          textAlign={'right'}
        >
          היסטוריית הודעות
        </Typography>
      </Stack>
      <WhatsAppDialog
        open={settingsDialog}
        onClose={handleCloseSettingsDialog}
      />
    </motion.div>
  );
};

export default WhatsAppSidebar;
